import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckIcon, DuplicateIcon } from "@heroicons/react/outline";
import { copyToClipboard } from "../../../common/utils/clipboard";
import type { IScormGuideInput } from "../scormImportGuide";
import { buildScormImportGuide, scormImportGuideAsText } from "../scormImportGuide";

interface Props {
  input: IScormGuideInput;
  showCopyButton?: boolean;
}

const SECTION_TITLE_CLASS = "text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2";

const ScormImportGuide = ({ input, showCopyButton = false }: Props) => {
  const { t } = useTranslation();
  const guide = buildScormImportGuide(t, input);
  const [copied, setCopied] = useState(false);
  const copiedTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (copiedTimeout.current) clearTimeout(copiedTimeout.current);
    },
    [],
  );

  const handleCopy = async () => {
    await copyToClipboard(scormImportGuideAsText(t, guide));
    setCopied(true);
    if (copiedTimeout.current) clearTimeout(copiedTimeout.current);
    copiedTimeout.current = setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div id="scorm-import-guide" className="flex flex-col gap-4">
      <div>
        <p className={SECTION_TITLE_CLASS}>{t("scormImportGuide.stepsTitle")}</p>
        <ol className="text-xs text-gray-600 space-y-1.5 list-decimal pl-4 leading-relaxed">
          {guide.steps.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      </div>
      <div>
        <p className={SECTION_TITLE_CLASS}>{t("scormImportGuide.requirementsTitle")}</p>
        <ul className="text-xs text-gray-600 space-y-1.5 list-disc pl-4 leading-relaxed">
          {guide.requirements.map((requirement) => (
            <li key={requirement}>{requirement}</li>
          ))}
        </ul>
      </div>
      {showCopyButton && (
        <button
          type="button"
          onClick={() => void handleCopy()}
          className={`self-end py-2 px-4 rounded-lg text-sm font-medium transition-all ${
            copied ? "bg-green-500 text-white" : "btn-primary-fill"
          }`}
        >
          <span className="inline-flex items-center gap-1.5 justify-center">
            {copied ? (
              <CheckIcon className="w-4 h-4" strokeWidth={2} />
            ) : (
              <DuplicateIcon className="w-3.5 h-3.5" strokeWidth={2} />
            )}
            {t(copied ? "scormImportGuide.copied" : "scormImportGuide.copy")}
          </span>
        </button>
      )}
    </div>
  );
};

export default ScormImportGuide;
